/* RESPONSIBLE TEAM: team-inbox-experience */

import { action } from '@ember/object';
import type Transition from '@ember/routing/-private/transition';
import Route from '@ember/routing/route';
import { inject as service } from '@ember/service';
import { startTour } from 'embercom/lib/intercom-widget-helper';
import { InboxCategory } from 'embercom/models/data/inbox/inbox-categories';
import type Inbox from 'embercom/objects/inbox/inboxes/inbox';
import { parseUpdatedCount } from 'embercom/objects/inbox/inboxes/inbox';
import type InboxApi from 'embercom/services/inbox-api';
import type InboxState from 'embercom/services/inbox-state';
import type InboxCounters from 'embercom/services/inbox2-counters';
import type Inbox2OptIn from 'embercom/services/inbox2-opt-in';
import { type CountersUpdatedEvent } from 'embercom/services/nexus';
import type Nexus from 'embercom/services/nexus';
import { NexusEventName, CountersUpdatedSource } from 'embercom/services/nexus';
import type Session from 'embercom/services/session';
import type TracingService from 'embercom/services/tracing';
import storage from 'embercom/vendor/intercom/storage';
import getTransitionParams from 'embercom/lib/router/get-transition-params';
import type RouteInfo from '@ember/routing/route-info';
import type InboxRedirectionService from 'embercom/services/inbox-redirection-service';
import type Router from '@ember/routing/router-service';
import type ThemeService from 'embercom/services/theme-service';
import { ResponseError } from 'embercom/lib/inbox/requests';
import type IntlService from 'embercom/services/intl';
import { InboxType } from 'embercom/models/data/inbox/inbox-types';

export type ExtendedTransition = Transition & {
  targetName: string;
  intent: { url: string | null };
  routeInfos: RouteInfo[];
};

// tour: https://app.intercom.com/a/apps/tx2p130c/outbound/tour/26372173
let collaboratorComposerOnboardingTourId = 370585;
// tour: https://app.intercom.com/a/apps/tx2p130c/outbound/tour/26408173
let collaboratorWithoutComposerOnboardingTourId = 371166;
let collaboratorWithoutComposerOnboardingLocalStorage = 'collaboratorOnboardingWithoutComposer';
let conversationRoute = 'inbox.workspace.inbox.inbox.conversation.conversation';
let inboxRoute = 'inbox.workspace.inbox.inbox.index';
let singleConversationRoute = 'inbox.workspace.inbox.conversation.conversation';

export default class InboxRoute extends Route {
  @service declare inboxApi: InboxApi;
  @service declare inboxState: InboxState;
  @service declare nexus: Nexus;
  @service declare session: Session;
  @service declare inbox2OptIn: Inbox2OptIn;
  @service declare inbox2Counters: InboxCounters;
  @service declare tracing: TracingService;
  @service declare router: Router;
  @service declare inboxRedirectionService: InboxRedirectionService;
  @service declare themeService: ThemeService;
  @service declare notificationsService: $TSFixMe;
  @service declare intl: IntlService;

  buildRouteInfoMetadata() {
    return {
      responsibleTeam: 'team-inbox',
      react: this.reactEnabled(),
    };
  }

  private reactEnabled() {
    return this.session.isWorkspaceLoaded && this.inboxState.showReactConversationList;
  }

  async model(params: { inbox_category: InboxCategory; inbox_id: string }): Promise<Inbox | null> {
    if (this.reactEnabled()) {
      return null;
    }
    this.tracing.tagRootSpan({ inbox_category: params.inbox_category, inbox_id: params.inbox_id });

    return await this.inboxApi.fetchInbox(params.inbox_category, params.inbox_id);
  }

  @action error(error: Error) {
    if (error instanceof ResponseError && error.response.status === 404) {
      this.notificationsService.notifyError(
        this.intl.t('inbox.notifications.failed-loading-inbox'),
      );
      this.transitionTo('inbox.workspace.inbox.inbox', InboxCategory.Shared, InboxType.All);
      return;
    }

    return true;
  }

  serialize(inbox: Inbox | null) {
    if (!inbox) {
      return {};
    }
    return {
      inbox_category: inbox.category,
      inbox_id: inbox.id,
    };
  }

  async beforeModel(transition: ExtendedTransition) {
    if (this.reactEnabled()) {
      return;
    }
    this.inboxState.activeConversation = undefined;

    if (this.isDeeplink(transition) && !this.isRefresh(transition)) {
      let target = this.getTransitionTarget(transition);
      if (target) {
        if (window.location.hash) {
          this.inboxRedirectionService.setConversationPartAnchor(window.location.hash);
          try {
            localStorage.setItem('inbox:conversation-part-anchor', window.location.hash);
          } catch {
            void 0;
          }
        }
        let queryParams: Record<string, string> = {};
        let sidebarTab = transition.to?.queryParams?.['sidebar_tab'];
        if (sidebarTab) {
          queryParams['sidebar_tab'] = sidebarTab;
        }
        this.transitionTo(singleConversationRoute, target, { queryParams });
      }
    }
  }

  private isDeeplink(transition: ExtendedTransition): boolean {
    return transition.targetName === conversationRoute;
  }

  private isRefresh(transition: ExtendedTransition): boolean {
    if (!transition.intent.url) {
      return false;
    }
    let target = this.extractLastUrlPathPart(transition.intent.url);
    let lastViewed;
    try {
      lastViewed = sessionStorage.getItem('inbox2:last-viewed-conversation');
    } catch (e) {
      // Nothing to do here
    }
    if (!lastViewed) {
      return false;
    }
    return target === lastViewed;
  }

  private getTransitionTarget(transition: ExtendedTransition): string | undefined {
    let transitionParams: Partial<Record<string, any>> = getTransitionParams(transition);
    if (transition.intent.url) {
      return this.extractLastUrlPathPart(transition.intent.url);
    } else if (transitionParams[singleConversationRoute]) {
      return transitionParams[singleConversationRoute].conversation_id;
    } else if (transitionParams[conversationRoute]) {
      return transitionParams[conversationRoute].conversation_id;
    }
    return;
  }

  private extractLastUrlPathPart(url: string): string | undefined {
    return url.split('/').pop()?.split('?')[0];
  }

  afterModel(inbox: Inbox | null, transition: Transition) {
    if (this.reactEnabled() || !inbox) {
      return;
    }
    if (this.session.showLightInbox) {
      if (this.showOnboardingWithComposerForCollaborator(transition.to.name, inbox.count || 0)) {
        startTour(collaboratorComposerOnboardingTourId);
        // collaborator is opted in if collaborator saw the full onboarding tour
        this.inbox2OptIn.optIn();
      } else if (this.showOnboardingWithOutComposerForCollaborator) {
        startTour(collaboratorWithoutComposerOnboardingTourId);
        this.setCollaboratorOnboardingWithoutComposerStatus();
      }
    }
    this.inboxState.setInbox(inbox);
  }

  activate() {
    this.nexus.addListener(NexusEventName.CountersUpdated, this.handleCounterUpdatedEvent);
  }

  deactivate() {
    this.inboxState.clearInbox();
    this.nexus.removeListener(NexusEventName.CountersUpdated, this.handleCounterUpdatedEvent);
  }

  @action
  private async handleCounterUpdatedEvent(e: CountersUpdatedEvent) {
    if (!this.inboxState.activeInbox) {
      return;
    }

    let data = e.eventData;
    if (data.source === CountersUpdatedSource.Database) {
      return;
    }

    let currentInboxCount = parseUpdatedCount([this.inboxState.activeInbox], data)[0];
    if (currentInboxCount !== undefined) {
      this.inbox2Counters.updateCount(this.inboxState.activeInbox, currentInboxCount);
    }
  }

  private showOnboardingWithComposerForCollaborator(transitionName: string, inboxCount: number) {
    return (
      (transitionName === conversationRoute || (transitionName === inboxRoute && inboxCount > 0)) &&
      this.inbox2OptIn.hasNeverSeenInbox2
    );
  }

  private get showOnboardingWithOutComposerForCollaborator() {
    return !this.collaboratorDidOnboardingWithoutComposer && this.inbox2OptIn.hasNeverSeenInbox2;
  }

  private get collaboratorDidOnboardingWithoutComposer() {
    return !!storage.get(collaboratorWithoutComposerOnboardingLocalStorage);
  }

  private setCollaboratorOnboardingWithoutComposerStatus() {
    return storage.set(collaboratorWithoutComposerOnboardingLocalStorage, true);
  }
}
